/* eslint-disable max-len */
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetEtaiement, useGetEtaiementsList } from '~/hooks/etaiements';
import styles from './mobilization-form.module.scss';

import {
  ErrorField,
  InputNumber,
  StyledSelect,
} from '~/lib/HooksFormFields';
import PreviewField from '~/components/PreviewField';
import { IOption } from '~/types/options';
import { IPhase, SummaryStep } from '~/types/estimates';

const MobilizationForm = ({ isPreview } : { isPreview: boolean }) => {
  const { control, watch, setValue } = useFormContext();
  const { id, phaseId } = useParams();
  const { data: etaiement } = useGetEtaiement(id || '');
  const phase : IPhase | undefined = useMemo(() => {
    const found = etaiement?.phases.find((d: IPhase) => d._id === phaseId);
    return found;
  }, [etaiement, phaseId]);
  const type = watch('mobilization.type');
  const calculMode = watch('mobilization.calculMode');
  const { data: list = { mobilization: {} } } = useGetEtaiementsList();
  const {
    mobilization,
    mobilizationModes,
    mobilizationRythmes = [],
    umbrellaMobilizationRythmes = [],
  } = list.mobilization;

  const handlingTypes = list.handlingTypes || [];

  const rythmes = mobilizationRythmes.filter((r :any) => {
    if (r.isSuspendu && calculMode !== 'tonnage') {
      return null;
    }
    return r;
  });

  const umbrellaRythmes = umbrellaMobilizationRythmes.filter((r :any) => {
    if (r.isSuspendu && calculMode !== 'tonnage') {
      return null;
    }
    return r;
  });

  const rythmesOptions = phase?.type === 'umbrella' ? umbrellaRythmes : rythmes;

  const stepAssembly :any = phase?.summary?.steps.find((s: SummaryStep) => s.value === 'assembly');
  const stepDisassembly :any = phase?.summary?.steps.find((s: SummaryStep) => s.value === 'disassembly');

  const handlingTypeAssemblyRatio = handlingTypes.find((h: any) => h.value === phase?.handling?.assembly?.type)?.ratio;
  const handlingTypeDisassemblyRatio = handlingTypes.find((h: any) => h.value === phase?.handling?.disassembly?.type)?.ratio;

  const tonnage = typeof phase?.dimension?.tonnage === 'number' ? phase?.dimension?.tonnage : 0;
  const assemblyRatio = typeof handlingTypeAssemblyRatio === 'number' && handlingTypeAssemblyRatio > 0 ? handlingTypeAssemblyRatio : null;
  const disassemblyRatio = typeof handlingTypeDisassemblyRatio === 'number' && handlingTypeDisassemblyRatio > 0 ? handlingTypeDisassemblyRatio : null;

  const assemblyPercentage = typeof phase?.handling?.assembly?.percentage === 'number'
    ? phase?.handling?.assembly?.percentage
    : 0;
  const disassemblyPercentage = typeof phase?.handling?.disassembly?.percentage === 'number'
    ? phase?.handling?.disassembly?.percentage
    : 0;

  const handlingDurationAssembly = tonnage && assemblyRatio ? ((tonnage / assemblyRatio) * 7.5) * (assemblyPercentage / 100) : 0;
  const handlingDurationDisassembly = tonnage && disassemblyRatio ? ((tonnage / disassemblyRatio) * 7.5) * (disassemblyPercentage / 100) : 0;

  const baseAssemblyDuration = typeof stepAssembly?.duration === 'number' ? stepAssembly.duration : 0;
  const baseDisassemblyDuration = typeof stepDisassembly?.duration === 'number' ? stepDisassembly.duration : 0;
  const totalAssemblyDuration = baseAssemblyDuration + handlingDurationAssembly;
  const totalDisassemblyDuration = baseDisassemblyDuration + handlingDurationDisassembly;

  return (
    <div id="montage" className={styles.mobilization}>
      <h4>Montage/Démontage</h4>
      <div className={styles.row}>
        <div className={styles.containerField}>
        {isPreview
          ? <PreviewField
              label='Type de Montage'
              value={mobilization?.find((a:IOption) => a.value === phase?.mobilization?.type)?.label || '---'}
            />
          : <StyledSelect
            label='Type de Montage'
            name='mobilization.type'
            control={control}
            options={mobilization}
            handleChange={(opt) => {
              if (opt.value !== 'Partiel') {
                setValue('mobilization.assemblyTonnage', null);
                setValue('mobilization.disassemblyTonnage', null);
              }
            }}
          />
        }
        </div>
      </div>
      {type === 'Partiel'
        && <div className={styles.row}>
          <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Tonnage à monter'
              value={phase?.mobilization?.assemblyTonnage?.toString() || '---'}
            />
            : <InputNumber
              name='mobilization.assemblyTonnage'
              label='Tonnage à monter'
              control={control}
              max={phase?.dimension?.tonnage}
            />
          }
          </div>
          <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Tonnage à démonter'
              value={phase?.mobilization?.disassemblyTonnage?.toString() || '---'}
            />
            : <InputNumber
              name='mobilization.disassemblyTonnage'
              label='Tonnage à démonter'
              control={control}
              max={phase?.dimension?.tonnage}
            />
          }
          </div>
        </div>
      }
      {type !== 'Aucun'
        && <div className={styles.row}>
        <div className={styles.containerField}>
        {isPreview
          ? <PreviewField
              label='Mode de calul'
              value={mobilizationModes?.find((a:IOption) => a.value === watch('mobilization.calculMode'))?.label || '---'}
            />
          : <StyledSelect
            label='Mode de calul'
            name='mobilization.calculMode'
            control={control}
            options={mobilizationModes}
            handleChange={() => {
              setValue('mobilization.rythme', null);
            }}
          />
          }
           {calculMode === 'multi' && !phase?.dimension?.scaffoldingSurface
            && <ErrorField message="La surface n'est pas renseignée" />
          }
        </div>
        <div className={styles.containerField}>
        {isPreview
          ? <PreviewField
              label='Choix cadence'
              value={rythmesOptions.find((a:IOption) => a.value === watch('mobilization.rythme'))?.label || '---'}
            />
          : <StyledSelect
            label='Choix cadence'
            name='mobilization.rythme'
            control={control}
            options={rythmesOptions}
          />
        }
        </div>
        <div id="summary" className={styles.summary}>
          <div className={`${styles.row} ${styles.label}`}>
            <p></p>
            <p>Heures</p>
            <p>Nbre Jr Equipe 2</p>
            <p>Nbre Jr Equipe 3</p>
          </div>
          <div className={styles.row} >
            <p>Temps Montage</p>
            <p>{totalAssemblyDuration}</p>
            <p>{totalAssemblyDuration ? Math.ceil(totalAssemblyDuration / 15) : 0}</p>
            <p>{totalAssemblyDuration ? Math.ceil(totalAssemblyDuration / 22.5) : 0}</p>
          </div>
          <div className={styles.row} >
            <p>Temps Démontage</p>
            <p>{totalDisassemblyDuration}</p>
            <p>{totalDisassemblyDuration ? Math.ceil(totalDisassemblyDuration / 15) : 0}</p>
            <p>{totalDisassemblyDuration ? Math.ceil(totalDisassemblyDuration / 22.5) : 0}</p>
          </div>
        </div>
      </div>

      }
    </div>
  );
};

export default MobilizationForm;
